import type { ComponentProps } from 'react';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { FormFieldLegend } from './form-field-label';
import { FormFieldMessages } from './form-field-messages';
import type { BaseFormFieldProps } from './shared';
import { useFormField } from './shared';

interface RadioGroupInputProps extends ComponentProps<typeof RadioGroup>, BaseFormFieldProps {
  options: { label: string; value: string; disabled?: boolean }[];
  layout?: 'vertical' | 'grid';
}

function RadioGroupInput({
  className,
  label,
  helperText,
  helperTextPlacement = 'beforeControl',
  id,
  isRequired,
  showAsterisk,
  errorText,
  invalid,
  options,
  layout = 'vertical',
  ref,
  ...props
}: RadioGroupInputProps) {
  const {
    fieldId: groupId,
    helperId,
    errorId,
    describedBy,
  } = useFormField(id, helperText, errorText);
  const isInvalid = Boolean(invalid || errorText);
  const isDisabled = Boolean(props.disabled);
  const labelId = label ? `${groupId}-legend` : undefined;

  return (
    <div className={cn('grid w-full gap-1.5', className)}>
      <fieldset aria-invalid={isInvalid || undefined} aria-describedby={describedBy} id={groupId}>
        {label && <FormFieldLegend id={labelId} label={label} showAsterisk={showAsterisk} />}
        {helperText && helperTextPlacement === 'beforeControl' && (
          <FormFieldMessages helperText={helperText} helperId={helperId} />
        )}
        <RadioGroup
          aria-labelledby={labelId}
          aria-required={isRequired}
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
          ref={ref}
          {...props}
          className={cn(
            'gap-3 mt-2.5',
            layout === 'grid' ? 'grid grid-cols-1 md:grid-cols-2' : 'flex flex-col',
          )}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={cn(
                'flex items-center space-x-2',
                (isDisabled || option.disabled) && 'cursor-not-allowed opacity-50',
              )}
            >
              <RadioGroupItem
                value={option.value}
                id={`${groupId}-${option.value}`}
                aria-label={option.label}
                disabled={option.disabled}
              />
              <Label htmlFor={`${groupId}-${option.value}`} className="cursor-pointer font-normal">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
      {helperText && helperTextPlacement === 'afterControl' && (
        <FormFieldMessages helperText={helperText} helperId={helperId} />
      )}
      {errorText && <FormFieldMessages errorText={errorText} errorId={errorId} />}
    </div>
  );
}

export { RadioGroupInput };
